import { useEffect, useRef } from 'react'

interface TurnstileApi {
  render: (container: HTMLElement, options: {
    sitekey: string
    theme: 'auto'
    callback: (token: string) => void
    'expired-callback': () => void
    'error-callback': () => void
  }) => string
  remove: (widgetId: string) => void
}

declare global {
  interface Window {
    turnstile?: TurnstileApi
  }
}

let turnstileReady: Promise<TurnstileApi> | null = null

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile)
  if (turnstileReady) return turnstileReady

  turnstileReady = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-dhuwit-turnstile]')
    const script = existing ?? document.createElement('script')

    function finish() {
      if (window.turnstile) resolve(window.turnstile)
      else reject(new Error('Turnstile tidak tersedia.'))
    }

    script.addEventListener('load', finish, { once: true })
    script.addEventListener('error', () => reject(new Error('Turnstile gagal dimuat.')), { once: true })

    if (!existing) {
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
      script.async = true
      script.defer = true
      script.dataset.dhuwitTurnstile = 'true'
      document.head.appendChild(script)
    }
  })

  return turnstileReady
}

export function Turnstile({ siteKey, resetKey, onToken, onError }: {
  siteKey: string
  resetKey: number
  onToken: (token: string) => void
  onError: (message: string) => void
}) {
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let active = true
    let widgetId = ''
    onToken('')

    void loadTurnstile().then(
      (api) => {
        if (!active || !containerRef.current) return
        widgetId = api.render(containerRef.current, {
          sitekey: siteKey,
          theme: 'auto',
          callback: (token) => onToken(token),
          'expired-callback': () => onToken(''),
          'error-callback': () => {
            onToken('')
            onError('Verifikasi keamanan gagal. Muat ulang halaman lalu coba lagi.')
          },
        })
      },
      () => {
        if (active) onError('Verifikasi keamanan tidak dapat dimuat. Periksa koneksi lalu coba lagi.')
      },
    )

    return () => {
      active = false
      if (widgetId && window.turnstile) window.turnstile.remove(widgetId)
    }
  }, [onError, onToken, resetKey, siteKey])

  return <div className="turnstile-box" aria-label="Verifikasi keamanan"><div ref={containerRef} /></div>
}
